import { useState, useCallback, useRef } from 'react';
import {
  Database, Search, PanelLeftClose, Plus, Network,
  Trash2, ChevronsDownUp, Target,
  Sun, Moon, Settings, FileUp, LogOut, Users,
} from 'lucide-react';
import { resolveExpandTarget, scrollTreeKeyIntoView } from '@/lib/tree-navigation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { useTunnelStore } from '@/stores/tunnel-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { apiClient } from '@/api/client';
import { useDialogStore } from '@/stores/dialog-store';
import { ContextMenu, type ContextMenuItem } from '@/components/shared/ContextMenu';
import { buildTunnelActions } from '@/lib/actions/tunnel-actions';
import { useModalStore } from '@/stores/modal-store';
import { ConnectionTree } from './ConnectionTree';
import { SkeletonTreeRows } from '@/components/shared/Skeleton';
import { getAllPanes } from '@/stores/workspace-store';
import type { ConnectionConfig, TunnelConfig, WorkareaTab } from '@/types';

interface SidebarContextMenuState {
  position: { x: number; y: number };
  items: ContextMenuItem[];
}

interface SidebarProps {
  onNewConnection?: () => void;
  onEditConnection?: (connection: ConnectionConfig) => void;
  onNewTunnel?: () => void;
  onEditTunnel?: (tunnel: TunnelConfig) => void;
  onUploadFiles?: () => void;
  onOpenSettings?: () => void;
}

function SectionResizeHandle({ onResize }: { onResize: (delta: number) => void }) {
  const dragging = useRef(false);
  const lastY = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    lastY.current = e.clientY;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const delta = ev.clientY - lastY.current;
      lastY.current = ev.clientY;
      onResize(delta);
    };

    const handleMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
  }, [onResize]);

  return (
    <div
      onMouseDown={handleMouseDown}
      className="h-[5px] cursor-row-resize border-t border-subtle hover:bg-accent-blue/20 active:bg-accent-blue/30 transition-colors flex-shrink-0"
    />
  );
}

export function Sidebar({
  onNewConnection,
  onEditConnection,
  onNewTunnel,
  onEditTunnel,
  onUploadFiles,
  onOpenSettings,
}: SidebarProps) {
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const [searchFilter, setSearchFilter] = useState('');
  const [contextMenu, setContextMenu] = useState<SidebarContextMenuState | null>(null);
  // Only tunnels gets its own height now — saved queries moved into the
  // connection tree. Connections takes the remainder.
  const [tunnelsHeight, setTunnelsHeight] = useState(120);
  const openShareDialog = useModalStore((s) => s.openShareDialog);
  const queryClient = useQueryClient();

  const { data: connections = [], isLoading } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
  });

  const { data: tunnels = [], isLoading: tunnelsLoading } = useQuery<TunnelConfig[]>({
    queryKey: ['tunnels'],
    queryFn: () => apiClient.get<TunnelConfig[]>('/api/tunnels'),
  });

  const activeTunnels = useTunnelStore((s) => s.activeTunnels);
  const tunnelSetStatus = useTunnelStore((s) => s.setStatus);
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const openTab = useWorkspaceStore((s) => s.openTab);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const startTunnelMutation = useMutation({
    mutationFn: (tunnelId: string) =>
      apiClient.post<{ ok: boolean; localPort?: number }>(`/api/tunnels/${tunnelId}/start`),
    onMutate: (tunnelId) => {
      tunnelSetStatus(tunnelId, 'starting');
    },
    onSuccess: (data, tunnelId) => {
      tunnelSetStatus(tunnelId, 'running', { localPort: data.localPort });
    },
    onError: (err, tunnelId) => {
      const message = err instanceof Error ? err.message : 'Failed to start tunnel';
      tunnelSetStatus(tunnelId, 'error', { error: message });
    },
  });

  const stopTunnelMutation = useMutation({
    mutationFn: (tunnelId: string) =>
      apiClient.post<{ ok: boolean }>(`/api/tunnels/${tunnelId}/stop`),
    onSuccess: (_data, tunnelId) => {
      tunnelSetStatus(tunnelId, 'stopped');
    },
  });

  const deleteTunnelMutation = useMutation({
    mutationFn: (tunnelId: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/tunnels/${tunnelId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tunnels'] });
    },
  });

  const handleNewConnection = useCallback(() => {
    onNewConnection?.();
  }, [onNewConnection]);

  // Collapse every expanded tree node back to the flat connection list.
  // One-click reset for users who expanded a bunch of schemas / folders
  // and want to start over. Purely visual — doesn't touch connect status.
  const handleCollapseAll = useCallback(() => {
    useAppStore.getState().setTreeExpanded({});
  }, []);

  // Reveal + scroll-to the active tab's source object. Silent no-op when
  // the active tab has no home in the connections tree (a tunnel, a
  // terminal, nothing focused). Doesn't force a connect — if the
  // connection is disconnected the target keys still flip to expanded,
  // which triggers the normal cache-first render.
  const handleExpandToActive = useCallback(() => {
    // Find the active tab across ALL panes, not just the focused pane.
    // The focused pane's `activeTabId` is what the workspace store
    // surfaces by default, but users can have an active tab in a split
    // pane that isn't the focused one; we still want the icon to reveal
    // whatever's actually showing.
    const ws = useWorkspaceStore.getState();
    const allPanes = getAllPanes(ws.root);
    let activeTab: WorkareaTab | null = null;
    for (const pane of allPanes) {
      const t = pane.tabs.find((x) => x.id === pane.activeTabId);
      if (t) { activeTab = t; break; }
    }

    const target = resolveExpandTarget(activeTab, connections);
    // Leave these logs in for now — helpful when someone reports "Target
    // did nothing". Remove in a cleanup pass once the feature is stable.
    // eslint-disable-next-line no-console
    console.log('[expand-to-active] tab:', activeTab);
    // eslint-disable-next-line no-console
    console.log('[expand-to-active] target:', target);
    if (!target) return;

    const { treeExpanded } = useAppStore.getState();
    const next = { ...treeExpanded };
    for (const key of target.keysToExpand) next[key] = true;
    useAppStore.getState().setTreeExpanded(next);

    if (target.scrollKey) scrollTreeKeyIntoView(target.scrollKey);
  }, [connections]);

  const handleNewTunnel = useCallback(() => {
    onNewTunnel?.();
  }, [onNewTunnel]);

  const handleTunnelClick = useCallback(
    (tunnel: TunnelConfig) => {
      openTab({
        type: 'tunnel',
        id: `tunnel:${tunnel.id}`,
        tunnelId: tunnel.id,
        tunnelName: tunnel.name,
      });
    },
    [openTab],
  );

  const handleTunnelContextMenu = useCallback(
    (e: React.MouseEvent, tunnel: TunnelConfig) => {
      e.preventDefault();
      e.stopPropagation();
      const active = activeTunnels[tunnel.id];
      const status = active?.status ?? 'stopped';
      const sshCmd = `ssh -L ${tunnel.localPort ?? '?'}:localhost:${tunnel.port} ${tunnel.username}@${tunnel.host}`;

      const items = buildTunnelActions(tunnel, status, {
        onStart: () => startTunnelMutation.mutate(tunnel.id),
        onStop: () => stopTunnelMutation.mutate(tunnel.id),
        onOpenTerminal: () => {
          openTab({
            type: 'terminal',
            id: `terminal:${tunnel.id}:${Date.now()}`,
            tunnelId: tunnel.id,
            tunnelName: tunnel.name,
            title: `${tunnel.name} — Terminal`,
          });
        },
        onCopySSHCommand: () => navigator.clipboard.writeText(sshCmd),
        onEdit: () => onEditTunnel?.(tunnel),
        onShare: () => openShareDialog({
          resourceType: 'tunnel',
          resourceId: tunnel.id,
          resourceName: tunnel.name,
        }),
        onDelete: () => {
          useDialogStore.getState().showConfirm({
            title: 'Delete Tunnel',
            message: `This will permanently delete the SSH tunnel "${tunnel.name}". This action cannot be undone.`,
            confirmText: tunnel.name,
            danger: true,
            onConfirm: async () => {
              await new Promise<void>((resolve, reject) => {
                deleteTunnelMutation.mutate(tunnel.id, {
                  onSuccess: () => resolve(),
                  onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
                });
              });
            },
          });
        },
      }, active?.localPort);

      setContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
    },
    [activeTunnels, openTab, startTunnelMutation, stopTunnelMutation, deleteTunnelMutation, onEditTunnel, openShareDialog],
  );

  const handleTunnelsResize = useCallback((delta: number) => {
    // Drag down = shrink tunnels (give space to connections above)
    setTunnelsHeight((h) => Math.max(28, Math.min(300, h - delta)));
  }, []);

  const hasConnections = connections.length > 0;

  // Search only scopes connections (and their schema children). Tunnels are
  // infrastructure — you still want to start/stop them regardless of what
  // you're searching for in the connections list above.
  const filteredTunnels = tunnels;

  return (
    <>
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="h-[36px] flex items-center px-3 border-b border-subtle flex-shrink-0">
        <span className="text-sm font-bold text-text-primary tracking-tight">DBGo</span>
        <div className="flex-1" />
        <button
          className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          title="Sign out"
          onClick={() => apiClient.logout()}
        >
          <LogOut size={14} />
        </button>
        <button
          className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          title="Settings"
          onClick={onOpenSettings}
        >
          <Settings size={14} />
        </button>
        <button
          className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          title="Toggle theme"
          onClick={toggleTheme}
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </button>
        <button
          className="w-6 h-6 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
          title="Collapse sidebar (Cmd+B)"
          onClick={toggleSidebar}
        >
          <PanelLeftClose size={14} />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 flex-shrink-0">
        <div className="flex items-center gap-1.5 bg-dbgo-input border rounded-md px-2 py-1">
          <Search size={12} className="text-text-muted flex-shrink-0" />
          <input
            type="text"
            placeholder="Search..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="bg-transparent text-xs text-text-primary placeholder:text-text-muted outline-none w-full"
          />
        </div>
      </div>

      {/* Connections section */}
      <div className="px-3 py-1.5 flex items-center justify-between flex-shrink-0">
        <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
          Connections
        </span>
        <div className="flex items-center gap-0.5">
          <button
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
            title="Collapse all tree nodes"
            onClick={handleCollapseAll}
          >
            <ChevronsDownUp size={13} />
          </button>
          <button
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
            title="Expand to active tab"
            onClick={handleExpandToActive}
          >
            <Target size={13} />
          </button>
          <button
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
            title="Upload Files"
            onClick={onUploadFiles}
          >
            <FileUp size={13} />
          </button>
          <button
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
            title="New Connection"
            onClick={handleNewConnection}
          >
            <Plus size={13} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-1 min-h-[60px]">
        {isLoading && (
          <SkeletonTreeRows count={4} />
        )}
        {!isLoading && !hasConnections && (
          <div className="flex flex-col items-center justify-center py-6 text-text-muted">
            <Database size={24} className="mb-2 opacity-40" />
            <span className="text-xs">No connections yet</span>
            <span className="text-2xs mt-0.5">Click + to add one</span>
          </div>
        )}
        {!isLoading && hasConnections && (
          <ConnectionTree
            connections={connections}
            searchFilter={searchFilter}
            onEditConnection={onEditConnection}
          />
        )}
      </div>

      {/* Resize handle: connections <-> tunnels */}
      <SectionResizeHandle onResize={handleTunnelsResize} />

      {/* Tunnels section */}
      <div className="flex-shrink-0 flex flex-col" style={{ height: tunnelsHeight }}>
        <div className="px-3 py-1.5 flex items-center justify-between flex-shrink-0">
          <span className="text-2xs font-semibold text-text-secondary uppercase tracking-widest">
            Tunnels
          </span>
          <button
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
            title="New Tunnel"
            onClick={handleNewTunnel}
          >
            <Plus size={13} />
          </button>
        </div>
        <div className="px-1 pb-1 overflow-y-auto flex-1 min-h-0">
          {tunnelsLoading && (
            <SkeletonTreeRows count={2} />
          )}
          {!tunnelsLoading && filteredTunnels.length === 0 && (
            <div className="px-2 py-1">
              <span className="text-2xs text-text-muted">
                {tunnels.length === 0 ? 'No tunnels' : 'No matching tunnels'}
              </span>
            </div>
          )}
          {!tunnelsLoading && filteredTunnels.map((tunnel) => {
            const active = activeTunnels[tunnel.id];
            const status = active?.status ?? 'stopped';
            const isInUse = status === 'running' ||
              connections.some(
                (c) => c.tunnelId === tunnel.id && activeConnections[c.id]?.status === 'connected',
              );
            return (
              <button
                key={tunnel.id}
                onClick={() => handleTunnelClick(tunnel)}
                onContextMenu={(e) => handleTunnelContextMenu(e, tunnel)}
                className="w-full flex items-center gap-2 py-[3px] px-2 rounded-sm cursor-pointer hover:bg-dbgo-tab-hover transition-colors group select-none text-left"
                style={{
                  boxShadow: tunnel.colorTag ? `inset 3px 0 0 0 ${tunnel.colorTag}` : undefined,
                }}
              >
                <div className="w-[7px] h-[7px] flex-shrink-0">
                  <div
                    className={clsx(
                      'w-[7px] h-[7px] rounded-full',
                      status === 'running' && 'bg-accent-green shadow-[0_0_6px_#3dd68c]',
                      status === 'stopped' && 'bg-text-muted',
                      status === 'error' && 'bg-accent-red',
                      status === 'starting' && 'bg-accent-yellow animate-pulse',
                    )}
                  />
                </div>
                <Network size={14} className={clsx('flex-shrink-0', isInUse ? 'text-accent-green' : 'text-accent-teal')} />
                <span className="text-[12.5px] text-text-primary truncate flex-1 min-w-0">
                  {tunnel.name}
                </span>
                {tunnel.shared && (
                  <Users size={12} className="text-text-muted flex-shrink-0" aria-label="Shared with you">
                    <title>Shared with you</title>
                  </Users>
                )}
                {isInUse && (
                  <div className="w-[6px] h-[6px] rounded-full bg-accent-green flex-shrink-0" title="In use by a connection" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Saved Queries used to be a flat section here — it didn't scale when
          a user had many queries. Queries are now nested under each
          connection in the ConnectionTree (sibling of its databases), which
          matches how users think about them: "the queries for prod-db" not
          "this one big pile". */}
    </div>

    {contextMenu && (
      <ContextMenu
        items={contextMenu.items}
        position={contextMenu.position}
        onClose={closeContextMenu}
      />
    )}
    </>
  );
}
